import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, useAuth } from '@/contexts/AuthContext';
import { ProjectSupervisors } from '@/components/ProjectSupervisors';
import { formatINRFull, getStatusColor, formatStatus, PROJECT_STATUSES, MATERIAL_STATUSES, INSTALLATION_STATUSES, formatDate } from '@/lib/utils';
import { ArrowLeft, Edit2, Save, Package, Receipt, CheckCircle, Clock, Trash2, Plus, X } from 'lucide-react';

export default function ProjectDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [boqTracker, setBoqTracker] = useState(null);

  const fetchProject = () => {
    api.get(`/projects/${id}`).then(r => { setProject(r.data.project); setEditForm(r.data.project); }).catch(console.error).finally(() => setLoading(false));
  };

  const fetchBoqTracker = () => {
    api.get(`/po-invoices/boq-invoice-tracker/${id}`).then(r => setBoqTracker(r.data)).catch(console.error);
  };

  useEffect(() => { fetchProject(); }, [id]);
  useEffect(() => { if (tab === 'boq-tracker') fetchBoqTracker(); }, [tab]);

  const canEdit = ['DIRECTOR', 'SALES'].includes(user?.role);

  const updateProject = async () => {
    try {
      await api.patch(`/projects/${id}`, editForm);
      fetchProject();
      setEditMode(false);
    } catch (err) { alert(err.response?.data?.detail || 'Update failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-[#1B5486] border-t-transparent rounded-full" /></div>;
  if (!project) return <div className="text-center py-12 text-slate-500">Project not found</div>;

  const pnl = project.pnl || {};
  const isSupervisor = user?.role === 'SUPERVISOR';
  const allTabs = ['overview', 'boq', 'purchase-orders', 'invoices', 'boq-tracker', 'expenses', 'revenue', 'pnl', 'team'];
  const tabs = isSupervisor ? allTabs.filter(t => !['purchase-orders', 'invoices', 'boq-tracker', 'revenue', 'pnl'].includes(t)) : allTabs;
  const tabLabels = { 'overview': 'Overview', 'boq': 'BOQ', 'purchase-orders': 'POs', 'invoices': 'Invoices', 'boq-tracker': 'Invoice Tracker', 'expenses': 'Expenses', 'revenue': 'Revenue', 'pnl': 'P&L', 'team': 'Team' };

  return (
    <div data-testid="project-detail-page" className="space-y-4">
      <div className="flex items-center gap-3 min-w-0">
        <button onClick={() => navigate('/projects')} className="text-slate-400 hover:text-slate-600 flex-shrink-0"><ArrowLeft size={20} /></button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-semibold text-slate-900 font-heading truncate">{project.project_code}</h1>
            <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${getStatusColor(project.status)}`}>{formatStatus(project.status)}</span>
          </div>
          <p className="text-sm text-slate-500 truncate">{project.name}</p>
        </div>
      </div>

      <div className="flex border-b border-slate-200 gap-1 overflow-x-auto">
        {tabs.map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`project-tab-${t}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${tab === t ? 'border-[#1B5486] text-[#1B5486]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            {tabLabels[t] || t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white rounded-lg border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-900">Project Details</h3>
              {canEdit && !editMode && <button onClick={() => setEditMode(true)} className="text-sm text-[#1B5486] hover:underline flex items-center gap-1"><Edit2 size={14} /> Edit</button>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <InfoRow label="Client" value={project.client?.name} />
              <InfoRow label="Region" value={project.region?.name} />
              <InfoRow label="Location" value={project.location} />
              <InfoRow label="Category" value={formatStatus(project.category)} />
              <InfoRow label="PO Number" value={project.po_number} editing={editMode} editComponent={<input value={editForm.po_number || ''} onChange={e => setEditForm({...editForm, po_number: e.target.value})} className="input-field w-full" />} />
              <InfoRow label="Contract Value" value={formatINRFull(project.contract_value)} />
              <InfoRow label="Status" value={<span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(project.status)}`}>{formatStatus(project.status)}</span>}
                editing={editMode} editComponent={<select value={editForm.status} onChange={e => setEditForm({...editForm, status: e.target.value})} className="input-field w-full">
                  {PROJECT_STATUSES.map(s => <option key={s} value={s}>{formatStatus(s)}</option>)}</select>} />
              <InfoRow label="Material Status" value={<span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(project.material_status)}`}>{formatStatus(project.material_status)}</span>}
                editing={editMode} editComponent={<select value={editForm.material_status} onChange={e => setEditForm({...editForm, material_status: e.target.value})} className="input-field w-full">
                  {MATERIAL_STATUSES.map(s => <option key={s} value={s}>{formatStatus(s)}</option>)}</select>} />
              <InfoRow label="Installation" value={<span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(project.installation_status)}`}>{formatStatus(project.installation_status)}</span>}
                editing={editMode} editComponent={<select value={editForm.installation_status} onChange={e => setEditForm({...editForm, installation_status: e.target.value})} className="input-field w-full">
                  {INSTALLATION_STATUSES.map(s => <option key={s} value={s}>{formatStatus(s)}</option>)}</select>} />
              <InfoRow label="Start Date" value={formatDate(project.start_date)} />
              <InfoRow label="Expected End" value={formatDate(project.expected_end_date)} />
            </div>
            {editMode && (
              <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                <button onClick={() => setEditMode(false)} className="px-4 py-2 border border-slate-200 rounded-md text-sm">Cancel</button>
                <button onClick={updateProject} className="px-4 py-2 bg-[#1B5486] text-white rounded-md text-sm font-medium hover:bg-[#144066] flex items-center gap-1"><Save size={14} /> Save</button>
              </div>
            )}
          </div>

          <div className="space-y-4">
            {!isSupervisor && (
            <div className="bg-white rounded-lg border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Financial Summary</h3>
              <div className="space-y-2">
                <SummaryRow label="Contract Value" value={formatINRFull(pnl.contract_value)} />
                <SummaryRow label="Revenue Received" value={formatINRFull(pnl.revenue_received)} color="text-emerald-600" />
                <SummaryRow label="Total Cost" value={formatINRFull(pnl.total_cost)} color="text-red-500" />
                <div className="border-t border-slate-100 pt-2">
                  <SummaryRow label="Gross Profit" value={formatINRFull(pnl.gross_profit)} color={pnl.gross_profit >= 0 ? 'text-green-600' : 'text-red-600'} bold />
                  <SummaryRow label="Margin" value={`${pnl.gross_margin_percent}%`} color={pnl.gross_margin_percent >= 0 ? 'text-green-600' : 'text-red-600'} />
                </div>
                <div className="border-t border-slate-100 pt-2">
                  <SummaryRow label="Outstanding" value={formatINRFull(pnl.outstanding)} color="text-amber-600" />
                  <SummaryRow label="Bank Expenses" value={formatINRFull(pnl.bank_expenses)} />
                  <SummaryRow label="Cash Expenses" value={formatINRFull(pnl.cash_expenses)} />
                </div>
              </div>
            </div>
            )}
          </div>
        </div>
      )}

      {tab === 'boq' && (
        <ProjectBOQTab projectId={id} boqItems={project.boq_items || []} isSupervisor={isSupervisor} canEdit={canEdit} onRefresh={fetchProject} />
      )}

      {tab === 'purchase-orders' && (
        <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="project-po-tab">
          <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2"><Package size={16} className="text-[#1B5486]" /> Purchase Orders</h3>
          {(project.purchase_orders || []).length === 0 ? (
            <div className="text-center py-8 text-sm text-slate-400">No purchase orders for this project</div>
          ) : (
            <>
              {['SUPPLY', 'SERVICE'].map(type => {
                const pos = (project.purchase_orders || []).filter(p => p.po_type === type);
                if (pos.length === 0) return null;
                return (
                  <div key={type} className="mb-4">
                    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2 flex items-center gap-1">
                      <span className={`w-2 h-2 rounded-full ${type === 'SUPPLY' ? 'bg-blue-500' : 'bg-purple-500'}`} />
                      {type} POs ({pos.length})
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="border-b border-slate-200">
                          <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">PO Number</th>
                          <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Vendor</th>
                          <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Date</th>
                          <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500">PO Value</th>
                          <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Total (incl GST)</th>
                          <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Status</th>
                        </tr></thead>
                        <tbody>
                          {pos.map(po => (
                            <tr key={po.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`project-po-${po.id}`}>
                              <td className="py-2 px-3 font-mono text-xs text-[#1B5486] font-medium">{po.po_number}</td>
                              <td className="py-2 px-3">{po.vendor_name || '-'}</td>
                              <td className="py-2 px-3 text-slate-500">{formatDate(po.po_date)}</td>
                              <td className="py-2 px-3 text-right font-mono">{formatINRFull(po.po_value)}</td>
                              <td className="py-2 px-3 text-right font-mono font-medium">{formatINRFull(po.total_amount)}</td>
                              <td className="py-2 px-3"><span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(po.status)}`}>{formatStatus(po.status)}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {tab === 'invoices' && (
        <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="project-invoices-tab">
          <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2"><Receipt size={16} className="text-emerald-600" /> Invoices</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-200">
                <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Invoice #</th>
                <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Type</th>
                <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Date</th>
                <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Total</th>
                <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500">TDS</th>
                <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Net Receivable</th>
                <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Status</th>
              </tr></thead>
              <tbody>
                {(project.invoices || []).map(inv => (
                  <tr key={inv.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`project-inv-${inv.id}`}>
                    <td className="py-2 px-3 font-mono text-xs text-emerald-700 font-medium">{inv.invoice_number}</td>
                    <td className="py-2 px-3"><span className={`text-xs px-1.5 py-0.5 rounded ${
                      inv.invoice_type === 'SUPPLY' ? 'bg-blue-100 text-blue-700' :
                      inv.invoice_type === 'SERVICE' ? 'bg-purple-100 text-purple-700' :
                      inv.invoice_type === 'ADVANCE' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                    }`}>{inv.invoice_type}</span></td>
                    <td className="py-2 px-3 text-slate-500">{formatDate(inv.invoice_date)}</td>
                    <td className="py-2 px-3 text-right font-mono font-medium">{formatINRFull(inv.total_amount)}</td>
                    <td className="py-2 px-3 text-right font-mono text-red-500">{inv.tds_deducted ? formatINRFull(inv.tds_deducted) : '-'}</td>
                    <td className="py-2 px-3 text-right font-mono text-emerald-600 font-medium">{formatINRFull(inv.net_receivable || inv.total_amount)}</td>
                    <td className="py-2 px-3"><span className={`text-xs px-2 py-0.5 rounded-full ${
                      inv.status === 'DRAFT' ? 'bg-slate-100 text-slate-600' :
                      inv.status === 'SUBMITTED' ? 'bg-blue-100 text-blue-700' :
                      inv.status === 'ACKNOWLEDGED' ? 'bg-amber-100 text-amber-700' :
                      'bg-green-100 text-green-700'
                    }`}>{formatStatus(inv.status)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(project.invoices || []).length === 0 && <div className="text-center py-8 text-sm text-slate-400">No invoices for this project</div>}
          </div>
        </div>
      )}

      {tab === 'boq-tracker' && (
        <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="project-boq-tracker-tab">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">BOQ Invoice Tracker</h3>
          {boqTracker ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500 mb-1">Total Items</p>
                  <p className="text-xl font-semibold text-slate-900">{boqTracker.summary.total_items}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-green-600 mb-1 flex items-center justify-center gap-1"><CheckCircle size={12} /> Fully Invoiced</p>
                  <p className="text-xl font-semibold text-green-700">{boqTracker.summary.fully_invoiced}</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-amber-600 mb-1 flex items-center justify-center gap-1"><Clock size={12} /> Pending</p>
                  <p className="text-xl font-semibold text-amber-700">{boqTracker.summary.pending}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-blue-600 mb-1">Remaining Amount</p>
                  <p className="text-lg font-semibold text-blue-700 font-mono">{formatINRFull(boqTracker.summary.total_remaining)}</p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm font-mono">
                  <thead><tr className="border-b border-slate-200">
                    <th className="text-left py-2 px-2 text-xs uppercase tracking-wider text-slate-500">S.No</th>
                    <th className="text-left py-2 px-2 text-xs uppercase tracking-wider text-slate-500">Description</th>
                    <th className="text-right py-2 px-2 text-xs uppercase tracking-wider text-slate-500">Quoted Qty</th>
                    <th className="text-right py-2 px-2 text-xs uppercase tracking-wider text-slate-500">Quoted Amt</th>
                    <th className="text-right py-2 px-2 text-xs uppercase tracking-wider text-slate-500">Invoiced Qty</th>
                    <th className="text-right py-2 px-2 text-xs uppercase tracking-wider text-slate-500">Invoiced Amt</th>
                    <th className="text-right py-2 px-2 text-xs uppercase tracking-wider text-slate-500">Remaining</th>
                    <th className="text-center py-2 px-2 text-xs uppercase tracking-wider text-slate-500">Status</th>
                  </tr></thead>
                  <tbody>
                    {boqTracker.items.map(item => (
                      <tr key={item.id} className={`border-b border-slate-50 ${item.fully_invoiced ? 'bg-green-50/40' : 'bg-amber-50/40'}`} data-testid={`boq-tracker-${item.id}`}>
                        <td className="py-2 px-2 text-slate-500">{item.sl_no}</td>
                        <td className="py-2 px-2 text-slate-900 font-sans">{item.description}</td>
                        <td className="py-2 px-2 text-right">{item.quoted_qty} {item.unit}</td>
                        <td className="py-2 px-2 text-right">{formatINRFull(item.quoted_amount)}</td>
                        <td className="py-2 px-2 text-right">{item.invoiced_qty}</td>
                        <td className="py-2 px-2 text-right">{formatINRFull(item.invoiced_amount)}</td>
                        <td className="py-2 px-2 text-right font-medium">{formatINRFull(item.remaining_amount)}</td>
                        <td className="py-2 px-2 text-center">
                          {item.fully_invoiced ? (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-sans" data-testid={`tracker-status-${item.id}`}>Invoiced</span>
                          ) : (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-sans" data-testid={`tracker-status-${item.id}`}>Pending</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="flex justify-center py-8"><div className="animate-spin w-6 h-6 border-2 border-[#1B5486] border-t-transparent rounded-full" /></div>
          )}
        </div>
      )}

      {tab === 'expenses' && (
        <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="project-expenses-tab">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Project Expenses</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-200">
                <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Date</th>
                <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Category</th>
                <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Description</th>
                <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Vendor</th>
                {!isSupervisor && <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Amount</th>}
                {!isSupervisor && <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Payment</th>}
              </tr></thead>
              <tbody>
                {(project.expenses || []).map(e => (
                  <tr key={e.id} className="border-b border-slate-50">
                    <td className="py-2 px-3 text-slate-500">{formatDate(e.date)}</td>
                    <td className="py-2 px-3"><span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{formatStatus(e.category)}</span></td>
                    <td className="py-2 px-3 text-slate-900">{e.description}</td>
                    <td className="py-2 px-3 text-slate-500">{e.vendor_name || '-'}</td>
                    {!isSupervisor && <td className="py-2 px-3 text-right font-mono">{formatINRFull(e.total_amount)}</td>}
                    {!isSupervisor && <td className="py-2 px-3"><span className="text-xs px-1.5 py-0.5 rounded bg-slate-100">{formatStatus(e.payment_mode)}</span></td>}
                  </tr>
                ))}
              </tbody>
            </table>
            {(project.expenses || []).length === 0 && <div className="text-center py-8 text-sm text-slate-400">No expenses recorded</div>}
          </div>
        </div>
      )}

      {tab === 'revenue' && (
        <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="project-revenue-tab">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Payments Received</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-200">
                <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Date</th>
                <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Amount</th>
                <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Mode</th>
                <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Invoice</th>
                <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Remarks</th>
              </tr></thead>
              <tbody>
                {(project.revenues || []).map(r => (
                  <tr key={r.id} className="border-b border-slate-50">
                    <td className="py-2 px-3 text-slate-500">{formatDate(r.date)}</td>
                    <td className="py-2 px-3 text-right font-mono font-medium text-emerald-600">{formatINRFull(r.amount)}</td>
                    <td className="py-2 px-3"><span className="text-xs px-1.5 py-0.5 rounded bg-slate-100">{r.payment_mode}</span></td>
                    <td className="py-2 px-3 text-slate-700">{r.invoice_number || '-'}</td>
                    <td className="py-2 px-3 text-slate-500">{r.remarks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(project.revenues || []).length === 0 && <div className="text-center py-8 text-sm text-slate-400">No payments received</div>}
          </div>
        </div>
      )}

      {tab === 'pnl' && (
        <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="project-pnl-tab">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Profit & Loss Statement</h3>
          <div className="max-w-xl space-y-1">
            <PnlRow label="A. Contract Value (PO Price)" value={pnl.contract_value} bold />
            <PnlRow label="B. Revenue Received" value={pnl.revenue_received} color="text-emerald-600" />
            <div className="border-t border-slate-200 my-2" />
            <PnlRow label="C. Material Cost (BOQ-linked)" value={pnl.material_cost_boq} indent />
            <PnlRow label="D. Material Cost (Non-BOQ)" value={pnl.material_cost_non_boq} indent />
            <PnlRow label="E. Labour Cost" value={pnl.labour_cost} indent />
            <PnlRow label="F. Transport" value={pnl.transport_cost} indent />
            <PnlRow label="G. Rental" value={pnl.rental_cost} indent />
            <PnlRow label="H. Other Direct Costs" value={pnl.other_direct_cost} indent />
            <PnlRow label="I. Total Direct Cost" value={pnl.total_direct_cost} bold />
            <PnlRow label="J. Allocated Overheads" value={pnl.allocated_overheads} indent />
            <PnlRow label="K. Total Cost" value={pnl.total_cost} bold color="text-red-600" />
            <div className="border-t-2 border-slate-300 my-2" />
            <PnlRow label="L. Gross Profit" value={pnl.gross_profit} bold color={pnl.gross_profit >= 0 ? 'text-green-600' : 'text-red-600'} />
            <PnlRow label="M. Gross Margin %" value={`${pnl.gross_margin_percent}%`} color={pnl.gross_margin_percent >= 0 ? 'text-green-600' : 'text-red-600'} isPercent />
            <PnlRow label="N. Cash Flow Gap" value={pnl.cash_flow_gap} color={pnl.cash_flow_gap >= 0 ? 'text-emerald-600' : 'text-red-600'} />
          </div>
        </div>
      )}

      {tab === 'team' && (
        <>
          <ProjectSupervisors projectId={id} canManage={user?.role === 'DIRECTOR'} />
          <ProjectTeamTab projectId={id} user={user} />
        </>
      )}
    </div>
  );
}

function ProjectTeamTab({ projectId, user }) {
  const [members, setMembers] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [selectedEmpId, setSelectedEmpId] = useState('');
  const [role, setRole] = useState('ENGINEER');
  const canEdit = ['DIRECTOR', 'HR_ADMIN'].includes(user?.role);
  const ROLES = ['ENGINEER', 'SUPERVISOR', 'TECHNICIAN', 'LABOUR', 'PROJECT_MANAGER'];

  useEffect(() => {
    api.get(`/projects/${projectId}/team`).then(r => setMembers(r.data.members || [])).catch(() => {});
    api.get('/employees').then(r => setAllEmployees(r.data.employees || [])).catch(() => {});
  }, [projectId]);

  const addMember = async () => {
    if (!selectedEmpId) return;
    try {
      await api.post(`/projects/${projectId}/team`, { employee_id: selectedEmpId, role });
      const res = await api.get(`/projects/${projectId}/team`);
      setMembers(res.data.members || []);
      setSelectedEmpId('');
    } catch (err) { alert('Failed to add member'); }
  };

  const removeMember = async (memberId) => {
    if (!window.confirm('Remove this team member?')) return;
    try {
      await api.delete(`/projects/${projectId}/team/${memberId}`);
      setMembers(members.filter(m => m.id !== memberId));
    } catch (err) { alert('Failed to remove member'); }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="project-team-tab">
      <h3 className="text-sm font-semibold text-slate-900 mb-4">Project Team</h3>
      {canEdit && (
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <select value={selectedEmpId} onChange={e => setSelectedEmpId(e.target.value)} className="input-field flex-1" data-testid="team-add-employee">
            <option value="">Select employee...</option>
            {allEmployees.filter(e => !members.some(m => m.employee_id === e.id)).map(e => (
              <option key={e.id} value={e.id}>{e.name} - {e.designation || e.employee_code}</option>
            ))}
          </select>
          <div className="flex gap-2">
            <select value={role} onChange={e => setRole(e.target.value)} className="input-field flex-1 sm:w-40" data-testid="team-add-role">
              {ROLES.map(r => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
            </select>
            <button onClick={addMember} disabled={!selectedEmpId} data-testid="team-add-btn"
              className="px-4 py-2 bg-[#1B5486] text-white rounded-md text-sm font-medium hover:bg-[#144066] disabled:opacity-50 whitespace-nowrap">Add</button>
          </div>
        </div>
      )}
      {members.length === 0 ? (
        <div className="text-center py-8 text-sm text-slate-400">No team members assigned</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {members.map(m => (
            <div key={m.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50/50" data-testid={`team-member-${m.id}`}>
              <div className="w-8 h-8 rounded-full bg-[#1B5486]/10 flex items-center justify-center text-[#1B5486] font-semibold text-xs">
                {(m.employee_name || '?')[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{m.employee_name}</p>
                <p className="text-xs text-slate-500">{m.role?.replace('_', ' ')}</p>
              </div>
              {canEdit && <button onClick={() => removeMember(m.id)} className="text-slate-300 hover:text-red-500"><Trash2 size={14} /></button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectBOQTab({ projectId, boqItems, isSupervisor, canEdit, onRefresh }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const BOQ_CATS = ['EQUIPMENT', 'MATERIAL', 'CABLE', 'ACCESSORY', 'SOFTWARE', 'OTHER'];

  const emptyForm = { sl_no: (boqItems.length + 1), description: '', make: '', model: '', unit: 'Nos', quoted_qty: 0, quoted_unit_price: 0, actual_unit_cost: 0, consumed_qty: 0, category: 'EQUIPMENT' };

  const handleAdd = async () => {
    try { await api.post(`/projects/${projectId}/boq`, form); setShowAdd(false); setForm({}); onRefresh(); } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };
  const handleEdit = async () => {
    try { await api.put(`/projects/${projectId}/boq/${editingId}`, form); setEditingId(null); setForm({}); onRefresh(); } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };
  const handleDelete = async (itemId) => {
    if (!window.confirm('Delete this BOQ item?')) return;
    try { await api.delete(`/projects/${projectId}/boq/${itemId}`); onRefresh(); } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };
  const startEdit = (item) => { setEditingId(item.id); setForm({...item}); setShowAdd(false); };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="project-boq-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-900">Project BOQ</h3>
        {canEdit && !showAdd && <button onClick={() => { setShowAdd(true); setForm(emptyForm); setEditingId(null); }} className="text-sm bg-[#1B5486] text-white px-3 py-1.5 rounded-md flex items-center gap-1" data-testid="add-boq-btn"><Plus size={14} /> Add Item</button>}
      </div>

      {(showAdd || editingId) && (
        <div className="border border-slate-200 rounded-lg p-4 mb-4 bg-slate-50/50 space-y-3">
          <h4 className="text-xs font-semibold text-slate-700 uppercase">{editingId ? 'Edit BOQ Item' : 'Add BOQ Item'}</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div><label className="block text-[10px] text-slate-500 mb-0.5">S.No</label>
              <input type="number" value={form.sl_no || ''} onChange={e => setForm({...form, sl_no: e.target.value})} className="input-field w-full text-sm" /></div>
            <div className="col-span-2 sm:col-span-3"><label className="block text-[10px] text-slate-500 mb-0.5">Description *</label>
              <input value={form.description || ''} onChange={e => setForm({...form, description: e.target.value})} className="input-field w-full text-sm" required /></div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div><label className="block text-[10px] text-slate-500 mb-0.5">Make</label>
              <input value={form.make || ''} onChange={e => setForm({...form, make: e.target.value})} className="input-field w-full text-sm" /></div>
            <div><label className="block text-[10px] text-slate-500 mb-0.5">Model</label>
              <input value={form.model || ''} onChange={e => setForm({...form, model: e.target.value})} className="input-field w-full text-sm" /></div>
            <div><label className="block text-[10px] text-slate-500 mb-0.5">Unit</label>
              <input value={form.unit || 'Nos'} onChange={e => setForm({...form, unit: e.target.value})} className="input-field w-full text-sm" /></div>
            <div><label className="block text-[10px] text-slate-500 mb-0.5">Category</label>
              <select value={form.category || 'EQUIPMENT'} onChange={e => setForm({...form, category: e.target.value})} className="input-field w-full text-sm">
                {BOQ_CATS.map(c => <option key={c} value={c}>{formatStatus(c)}</option>)}</select></div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div><label className="block text-[10px] text-slate-500 mb-0.5">Quoted Qty</label>
              <input type="number" value={form.quoted_qty || ''} onChange={e => setForm({...form, quoted_qty: e.target.value})} className="input-field w-full text-sm" /></div>
            <div><label className="block text-[10px] text-slate-500 mb-0.5">Quoted Rate (₹)</label>
              <input type="number" value={form.quoted_unit_price || ''} onChange={e => setForm({...form, quoted_unit_price: e.target.value})} className="input-field w-full text-sm" /></div>
            <div><label className="block text-[10px] text-slate-500 mb-0.5">Actual Cost (₹)</label>
              <input type="number" value={form.actual_unit_cost || ''} onChange={e => setForm({...form, actual_unit_cost: e.target.value})} className="input-field w-full text-sm" /></div>
            <div><label className="block text-[10px] text-slate-500 mb-0.5">Consumed Qty</label>
              <input type="number" value={form.consumed_qty || ''} onChange={e => setForm({...form, consumed_qty: e.target.value})} className="input-field w-full text-sm" /></div>
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={() => { setShowAdd(false); setEditingId(null); setForm({}); }} className="text-xs px-3 py-1.5 border border-slate-200 rounded-md">Cancel</button>
            <button onClick={editingId ? handleEdit : handleAdd} className="text-xs px-3 py-1.5 bg-[#1B5486] text-white rounded-md">{editingId ? 'Update' : 'Add'}</button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm font-mono min-w-[700px]">
          <thead><tr className="border-b border-slate-200">
            <th className="text-left py-2 px-2 text-xs uppercase tracking-wider text-slate-500">S.No</th>
            <th className="text-left py-2 px-2 text-xs uppercase tracking-wider text-slate-500">Description</th>
            <th className="text-right py-2 px-2 text-xs uppercase tracking-wider text-slate-500">Quoted Qty</th>
            <th className="text-right py-2 px-2 text-xs uppercase tracking-wider text-slate-500">Consumed</th>
            {!isSupervisor && <th className="text-right py-2 px-2 text-xs uppercase tracking-wider text-slate-500">Quoted Rate</th>}
            {!isSupervisor && <th className="text-right py-2 px-2 text-xs uppercase tracking-wider text-slate-500">Actual Cost</th>}
            {!isSupervisor && <th className="text-right py-2 px-2 text-xs uppercase tracking-wider text-slate-500 bg-amber-50">Actual Spent</th>}
            <th className="text-left py-2 px-2 text-xs uppercase tracking-wider text-slate-500">Category</th>
            {canEdit && <th className="w-16"></th>}
          </tr></thead>
          <tbody>
            {boqItems.map(item => (
              <tr key={item.id} className="border-b border-slate-50 hover:bg-slate-50">
                <td className="py-2 px-2 text-slate-500">{item.sl_no}</td>
                <td className="py-2 px-2 text-slate-900">{item.description}</td>
                <td className="py-2 px-2 text-right">{item.quoted_qty} {item.unit}</td>
                <td className="py-2 px-2 text-right">{item.consumed_qty}</td>
                {!isSupervisor && <td className="py-2 px-2 text-right">{formatINRFull(item.quoted_unit_price)}</td>}
                {!isSupervisor && <td className="py-2 px-2 text-right">{formatINRFull(item.actual_unit_cost)}</td>}
                {!isSupervisor && <td className="py-2 px-2 text-right bg-amber-50/50 font-medium" data-testid={`boq-actual-spent-${item.id}`}>{formatINRFull(item.actual_spent || 0)}</td>}
                <td className="py-2 px-2"><span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{formatStatus(item.category)}</span></td>
                {canEdit && (
                  <td className="py-2 px-2">
                    <div className="flex gap-1">
                      <button onClick={() => startEdit(item)} className="text-slate-400 hover:text-[#1B5486]" data-testid={`edit-boq-${item.id}`}><Edit2 size={13} /></button>
                      <button onClick={() => handleDelete(item.id)} className="text-slate-400 hover:text-red-500" data-testid={`delete-boq-${item.id}`}><Trash2 size={13} /></button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {boqItems.length === 0 && <div className="text-center py-8 text-sm text-slate-400">No BOQ items. {canEdit ? 'Click "Add Item" to add.' : ''}</div>}
      </div>
    </div>
  );
}

function InfoRow({ label, value, editing, editComponent }) {
  return (
    <div className="min-w-0">
      <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{label}</span>
      <div className="mt-1">{editing && editComponent ? editComponent : <p className="text-sm text-slate-900 break-all">{value || '-'}</p>}</div>
    </div>
  );
}

function SummaryRow({ label, value, color, bold }) {
  return (
    <div className="flex justify-between items-center">
      <span className={`text-xs text-slate-500 ${bold ? 'font-semibold' : ''}`}>{label}</span>
      <span className={`text-sm font-mono ${bold ? 'font-semibold' : ''} ${color || 'text-slate-900'}`}>{value}</span>
    </div>
  );
}

function PnlRow({ label, value, bold, color, indent, isPercent }) {
  return (
    <div className={`flex justify-between items-center py-1 ${indent ? 'pl-4' : ''}`}>
      <span className={`text-sm ${bold ? 'font-semibold text-slate-900' : 'text-slate-600'}`}>{label}</span>
      <span className={`text-sm font-mono ${bold ? 'font-semibold' : ''} ${color || 'text-slate-900'}`}>
        {isPercent ? value : formatINRFull(value)}
      </span>
    </div>
  );
}
